import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { updateFilterPartial } from '../queries';
import { APP_ROUTES } from '../constants/routes';
import { EventTypeIdentifier } from '../enums';
import { EventsFilter } from '../interfaces';
import { hash } from '../utils';
import { useEventTypes } from './useEventTypes';
import { useFilter } from './useFilter';

export const useLimitedFilters = (includeMatches: boolean, isInternal: boolean): Partial<EventsFilter> => {
  const currentFilter = useFilter();
  const eventTypes = useEventTypes();

  const availableTypes = eventTypes?.filter((type) => {
    if (includeMatches) {
      return type.identifier.includes('_MATCH');
    }
    return !type.identifier.includes('_MATCH');
  });

  const availableTypeNames = availableTypes?.map((t) => t.identifier);

  const location = useLocation();

  const selectedFilters = useMemo(() => {
    const filtered = availableTypeNames?.filter((t) => currentFilter?.selectedFilters?.includes(t));

    if (location.pathname === APP_ROUTES.groupsReview) {
      return [filtered[0] ?? availableTypeNames[0]];
    }

    if (filtered?.length === 0) {
      return availableTypeNames;
    }

    return filtered;
  }, [location.pathname, currentFilter?.selectedFilters, availableTypeNames?.length]);

  const reviewFilter: Partial<EventsFilter> = {
    status: isInternal ? undefined : 'null', // we cant have external review until human_review is true, so we can keep this field
    human_review: isInternal ? 'null' : undefined,
    sort_by: location.pathname === APP_ROUTES.matchesReviewGrid ? 'bus_stop_id' : undefined,
    selectedFilters,
  };

  useEffect(() => {
    // if some of the selected filters dont exist in availableTypes
    if (
      currentFilter.selectedFilters?.length === 0 ||
      currentFilter?.selectedFilters?.some((sf) => !availableTypeNames?.includes(sf as EventTypeIdentifier))
    ) {
      updateFilterPartial(reviewFilter);
    }
  }, [hash(reviewFilter), currentFilter?.selectedFilters, location.pathname]);

  return reviewFilter;
};

export const useFilterMatchesOnly = (isInternal = false) => useLimitedFilters(true, isInternal);
export const useFilterNonMatchesOnly = (isInternal = false) => useLimitedFilters(false, isInternal);
